import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { api, ApiError } from '../lib/api';
import { useSession } from '../lib/session';
import { Wordmark } from '../components/Wordmark';

export default function Login() {
  const { t } = useTranslation();
  const { login, notice } = useSession();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [tenantSlug, setTenantSlug] = useState('');
  const [needsTenant, setNeedsTenant] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      const { token } = await api<{ token: string }>('/auth/login', {
        method: 'POST',
        body: JSON.stringify({ email, password, ...(needsTenant && tenantSlug ? { tenantSlug } : {}) })
      });
      login(token);
    } catch (err) {
      if (err instanceof ApiError && err.code === 'NEEDS_TENANT') setNeedsTenant(true);
      setError(err instanceof ApiError ? err.message : t('errors.generic'));
    } finally {
      setBusy(false);
    }
  }

  const input = 'h-11 w-full rounded-md border border-[#C9C7C2] bg-white px-3 text-[15px] focus:border-accent';

  return (
    <main className="flex min-h-screen items-center justify-center bg-paper px-4 py-10">
      <div className="w-full max-w-sm">
        <div className="mb-8 flex justify-center"><Wordmark tone="dark" size="lg" /></div>
        <form onSubmit={submit} className="rounded-xl border border-line bg-white p-7 shadow-sm" noValidate>
          <h1 className="text-xl font-semibold">{t('login.title')}</h1>
          <p className="mt-1 text-sm text-muted">{t('login.subtitle')}</p>

          {notice && !error && (
            <p role="status" className="mt-4 rounded-md bg-[#FFF4E5] px-3 py-2 text-sm text-[#5A3200]">{notice}</p>
          )}

          <div className="mt-5 flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <label htmlFor="email" className="text-sm font-medium">{t('login.email')}</label>
              <input id="email" type="email" autoComplete="username" required value={email}
                onChange={e => setEmail(e.target.value)} className={input} />
            </div>
            <div className="flex flex-col gap-1.5">
              <label htmlFor="password" className="text-sm font-medium">{t('login.password')}</label>
              <input id="password" type="password" autoComplete="current-password" required value={password}
                onChange={e => setPassword(e.target.value)} className={input} />
            </div>
            {needsTenant && (
              <div className="flex flex-col gap-1.5">
                <label htmlFor="tenant" className="text-sm font-medium">{t('login.tenant')}</label>
                <input id="tenant" type="text" autoComplete="organization" value={tenantSlug}
                  onChange={e => setTenantSlug(e.target.value)} placeholder="ascendix" className={input} />
                <p className="text-xs text-muted">{t('login.tenantHelp')}</p>
              </div>
            )}
          </div>

          {error && <p role="alert" className="mt-4 text-sm font-medium text-[#8F1D1D]">{error}</p>}

          <button type="submit" disabled={busy || !email || !password}
            className="mt-6 h-11 w-full rounded-md bg-accent font-semibold text-white hover:bg-accent-dark disabled:opacity-60">
            {busy ? t('login.submitting') : t('login.submit')}
          </button>
          <p className="mt-4 text-center text-xs text-muted">
            <a href="https://horas.ascendix.com.mx" className="underline">{t('login.forgot')}</a>
          </p>
        </form>
      </div>
    </main>
  );
}
